'use client';

import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import {
  acceptSeedingProposalService,
  applyRecruitmentCampaignService,
  cancelRecruitmentCampaignService,
  rejectSeedingProposalService,
  submitSeedingContentService,
  updateDeliveryAddressService,
} from '@musinsa/services-curator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCuratorBroadcastPublisher } from '../../common';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { isMusinsaService } from '../utils/campaignProposalMusinsaAnalytics';
import { trackSeedingApplyClickMusinsa } from '../utils/seedingMusinsaAnalytics';

/** 시딩 상세(모집형/제안형) 캐시를 함께 무효화 — mutation 후 화면 상태 재조회. */
const useInvalidateSeedingDetail = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: curatorQueries.campaigns.seedingRecruitmentDetail._def,
    });
    queryClient.invalidateQueries({
      queryKey: curatorQueries.campaigns.seedingProposalDetail._def,
    });
    // 통일 상세(`/campaign/seeding/[campaignId]` = useSeedingDetail)는 별도 쿼리 키라
    // 함께 무효화해야 수락/거절/신청 후 화면 상태(예: 수락→CONFIRMED 상세)가 재조회된다.
    queryClient.invalidateQueries({
      queryKey: curatorQueries.campaigns.seedingCampaignDetail._def,
    });
    // 모집 중 캠페인 PLP — 신청 시 BE 가 영구 제외하므로 목록도 무효화해야, 신청 후
    // 뒤로가기로 목록 재진입 시 신청한 캠페인이 stale 캐시로 남아 노출되던 문제가 없다. (SID-4291)
    queryClient.invalidateQueries({
      queryKey: curatorQueries.campaigns.recruitment._def,
    });
    // 모집 카테고리 칩도 무효화 — 신청으로 마지막 캠페인이 빠진 카테고리 칩이 stale 로 남아,
    // 뒤로가기 복귀 시 빈 카테고리 칩이 노출(선택 시 0개)되던 문제 방지. (CMQA-7157)
    queryClient.invalidateQueries({
      queryKey: curatorQueries.campaigns.recruitmentCategories._def,
    });
  };
};

/** 모집형 시딩 캠페인 신청. */
export const useApplyRecruitmentCampaign = () => {
  const invalidate = useInvalidateSeedingDetail();
  const { service } = useDeviceInfo();
  // curatorId — ProposalList 선례: react-query dedupe 로 중복 호출 없음.
  const { data: userInfo } = useQuery({ ...curatorQueries.profile.userInfo });
  const curatorId = userInfo?.snsTitle ?? '';
  return useMutation({
    mutationFn: applyRecruitmentCampaignService,
    onSuccess: (_data, variables) => {
      invalidate();
      // 시딩 캠페인 상세 > 신청하기 클릭 (신청 완료 시점 적재) — IMPLEMENTATION.md ② #1
      const { campaignId } = variables;
      if (isMusinsaService(service)) {
        // GTM 신청하기(GA4 click_button) — brand_id 없음(onSuccess에서 DTO 미제공).
        trackSeedingApplyClickMusinsa({ campaignId, buttonName: '신청하기' });
      } else if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
          data: {
            page_name: 'curator_seeding_campaign_detail',
            section_name: 'CuratorSeedingApply',
            section_title: 'CuratorSeedingApply',
            button_id: 'curator_seeding_apply',
            button_name: '신청하기',
            on_site_content_id: campaignId,
            layout_id: curatorId,
            position: -1,
          },
        });
      }
    },
  });
};

/** 시딩 제안 수락. campaignId 는 상세 no.2(수락하기) 발화용(수락 onSuccess = 수락 완료 시점). */
export const useAcceptSeedingProposal = (campaignId: number) => {
  const invalidate = useInvalidateSeedingDetail();
  const { service } = useDeviceInfo();
  // curatorId — ProposalList 선례: react-query dedupe 로 중복 호출 없음.
  const { data: userInfo } = useQuery({ ...curatorQueries.profile.userInfo });
  const curatorId = userInfo?.snsTitle ?? '';
  return useMutation({
    mutationFn: acceptSeedingProposalService,
    onSuccess: () => {
      invalidate();
      // 시딩 상세 no.2 — 제안형 수락 완료 시점 적재(button_name='수락하기'). 모집형 신청하기와 동일 이벤트.
      if (isMusinsaService(service)) {
        // GTM(GA4 click_button). AMP 는 ProposeDetailContainer onSuccess 에서 발화(brandId 가용).
        trackSeedingApplyClickMusinsa({ campaignId, buttonName: '수락하기' });
      } else if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
          data: {
            page_name: 'curator_seeding_campaign_detail',
            section_name: 'CuratorSeedingApply',
            section_title: 'CuratorSeedingApply',
            button_id: 'curator_seeding_apply',
            button_name: '수락하기',
            on_site_content_id: campaignId,
            layout_id: curatorId,
            position: -1,
          },
        });
      }
    },
  });
};

/** 모집형 시딩 캠페인 신청 취소 (선정대기 등 신청 후 상태에서 자진 취소). */
export const useCancelRecruitmentCampaign = () => {
  const invalidate = useInvalidateSeedingDetail();
  return useMutation({
    mutationFn: cancelRecruitmentCampaignService,
    onSuccess: invalidate,
  });
};

/** 시딩 제안 거절. */
export const useRejectSeedingProposal = () => {
  const invalidate = useInvalidateSeedingDetail();
  return useMutation({
    mutationFn: rejectSeedingProposalService,
    onSuccess: invalidate,
  });
};

/** 시딩 캠페인 배송지 수정 — 선택 배송지를 서버 영속화 후 상세 재조회. (SID-4048/4114) */
export const useUpdateDeliveryAddress = () => {
  const invalidate = useInvalidateSeedingDetail();
  return useMutation({
    mutationFn: updateDeliveryAddressService,
    onSuccess: invalidate,
  });
};

/** 시딩 콘텐츠 링크 제출. */
export const useSubmitSeedingContent = () => {
  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  return useMutation({
    mutationFn: submitSeedingContentService,
    // 콘텐츠 등록은 29CM 인앱에서 부모 상세와 별도 웹뷰 레이어(별도 react-query 캐시)로 열린다.
    // 로컬 invalidate 만으론 부모 상세가 안 갱신되므로, BroadcastChannel 로 부모 웹뷰의 상세
    // 캐시까지 무효화한다. triggerQuerySync 는 발행과 동시에 로컬 캐시도 함께 무효화한다. (CMQA-7165)
    onSuccess: () =>
      triggerQuerySync({
        queryKeys: [
          curatorQueries.campaigns.seedingRecruitmentDetail._def,
          curatorQueries.campaigns.seedingProposalDetail._def,
          curatorQueries.campaigns.seedingCampaignDetail._def,
          // me 홈 '진행 중 캠페인' 목록 — 제출 후 "업로드 필요" 카드가 stale 로 잔존하던 문제. (CMQA-7202)
          curatorQueries.campaigns.inProgress().queryKey,
        ],
      }),
  });
};
